import axiosInterceptor from '../../utils/axios/axiosInterceptor';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { IRelatedTable, ITableData, IQueryLogResponse } from '../../types/table';
import { v4 as uuid } from 'uuid';
import { projectRunBasePath } from '../../services/projectRunsApi';

const api = () => axiosInterceptor(true);
export interface CoreAndRelatedTableState {
  isTableDataLoading: boolean;
  isDataAvailable: boolean;
  errorMessage: string;
  tableData: ITableData[];
  tableOptions: string[];
  isQueryLogDataAvailable: boolean;
}

const initialState: CoreAndRelatedTableState = {
  isTableDataLoading: false,
  isDataAvailable: false,
  errorMessage: '',
  tableData: [],
  tableOptions: [],
  isQueryLogDataAvailable: false,
};

export interface IRequestBody {
  tableId: string;
  tableName: string;
  isCore: boolean;
  isSelected: boolean;
  relatedTableId: string;
  action: ActionType;
}

export enum ActionType {
  ADD_CORE = 'ADD_CORE',
  REMOVE_CORE = 'REMOVE_CORE',
  ADD_RELATION = 'ADD_RELATION',
  REMOVE_RELATION = 'REMOVE_RELATION',
}

export const coreAndRelatedTableSlice = createSlice({
  name: 'coreAndRelatedTable',
  initialState,
  reducers: {
    resetCoreAndRelatedToInitial: () => ({ ...initialState }),
    setIsTableDataLoading: (state, action: PayloadAction<boolean>) => {
      state.isTableDataLoading = action.payload;
    },
    setIsDataAvailable: (state, action: PayloadAction<boolean>) => {
      state.isDataAvailable = action.payload;
    },
    setIsQueryLogDataAvailable: (state, action: PayloadAction<boolean>) => {
      state.isQueryLogDataAvailable = action.payload;
    },
    setErrorMessage: (state, action: PayloadAction<string>) => {
      state.errorMessage = action.payload;
    },
    setTableData: (state, action: PayloadAction<ITableData[]>) => {
      const updatedTableData = action.payload.map((table) => ({
        ...table,
        mapping: 0,
      }));

      if (updatedTableData.length > 0) {
        // Sort tables marked as core by number of related tables and keep only top 5
        const MAX_CORE_TABLES = 5;
        const coreTables = updatedTableData
          .filter((table) => table.isCore)
          .map((table) => ({
            key: table.key,
            relatedCount: table.relatedTables?.length || 0,
          }))
          .sort((a, b) => b.relatedCount - a.relatedCount);
        
        // Get keys of top 5 core tables
        const topCoreTableKeys = new Set(
          coreTables.slice(0, MAX_CORE_TABLES).map((t) => t.key)
        );
        
        // Reset isCore for tables not in top 5
        updatedTableData.forEach((table) => {
          if (table.isCore && !topCoreTableKeys.has(table.key)) {
            table.isCore = false;
          }
        });

        updatedTableData.forEach((table) => {
          if (table.isCore) {
            table.mapping = table.mapping + 1;
            table.relatedTables.forEach((relatedTable) => {
              const existingTable = updatedTableData.find(
                (selectedTable) => selectedTable.key === relatedTable.key
              );
              if (existingTable) {
                existingTable.mapping++;
                existingTable.isSelected = true;
              } else {
                updatedTableData.push({
                  key: relatedTable.key,
                  name: relatedTable.name,
                  mapping: 1,
                  isCore: false,
                  isSelected: true,
                  tableFoundInDB: false,
                  relatedTables: [],
                });
              }
            });
          }
        });
      }
      state.tableData = updatedTableData;
      const tableOptions = updatedTableData
        .filter((table: ITableData) => table.tableFoundInDB)
        .map((table: ITableData) => table.name)
        .sort();
      state.tableOptions = tableOptions;
    },
    mergeQueryLogData: (
      state,
      action: PayloadAction<{
        coreTables: Set<string>;
        relationshipsMap: Map<string, Set<string>>;
      }>
    ) => {
      const { coreTables, relationshipsMap } = action.payload;
      
      // Process all core tables from query log first, then apply top 5 filter at the end
      coreTables.forEach((coreTableName) => {
        if (!coreTableName) return;
        
        const existingCoreTable = state.tableData.find(
          (table) => table.name?.toLowerCase() === coreTableName?.toLowerCase()
        );

        if (existingCoreTable) {
          // If the table wasn't already a core table, increment its mapping
          if (!existingCoreTable.isCore) {
            existingCoreTable.mapping++;
          }
          existingCoreTable.isCore = true;
          existingCoreTable.isSelected = true;
          existingCoreTable.tableFoundInDB = true;
          if (!state.tableOptions.includes(coreTableName)) {
            state.tableOptions = [...state.tableOptions, existingCoreTable.name];
          }
        } else {
          state.tableData.push({
            key: uuid(),
            name: coreTableName,
            mapping: 1,
            isCore: true,
            isSelected: true,
            tableFoundInDB: true,
            relatedTables: [],
          });
          if (!state.tableOptions.includes(coreTableName)) {
            state.tableOptions = [...state.tableOptions, coreTableName];
          }
        }
      });

      coreTables.forEach((coreTableName) => {
        if (!coreTableName) return;
        
        const coreTable = state.tableData.find(
          (table) => table.name?.toLowerCase() === coreTableName?.toLowerCase()
        );

        if (!coreTable) return;

        const relatedTableNames = relationshipsMap.get(coreTableName);
        if (!relatedTableNames || relatedTableNames.size === 0) return;

        relatedTableNames.forEach((relTableName) => {
          if (!relTableName) return;
          
          // Check if this relationship already exists
          const existingRelationship = coreTable.relatedTables.find(
            (relTable) => relTable.name?.toLowerCase() === relTableName?.toLowerCase()
          );

          if (!existingRelationship) {
            coreTable.relatedTables.push({
              key: uuid(),
              name: relTableName,
              systemGeneratedCorrelation: true,
              query_reference: true,
            });

            // Ensure the related table exists in tableData and increment its mapping
            let relatedTable = state.tableData.find(
              (table) => table.name?.toLowerCase() === relTableName?.toLowerCase()
            );

            if (!relatedTable) {
              state.tableData.push({
                key: uuid(),
                name: relTableName,
                mapping: 1,
                isCore: false,
                isSelected: true,
                tableFoundInDB: true,
                relatedTables: [],
              });
              if (!state.tableOptions.includes(coreTableName)) {
                state.tableOptions = [...state.tableOptions, relTableName];
              }
            } else {
              // Increment mapping since this table is now being referenced by another core table
              relatedTable.mapping++;
              relatedTable.isSelected = true;
            }
          }
        });
      });

      // Final filter: Ensure only top 5 core tables by related table count remain
      const MAX_CORE_TABLES = 5;
      const allCoreTables = state.tableData
        .filter((table) => table.isCore)
        .map((table) => ({
          key: table.key,
          relatedCount: table.relatedTables?.length || 0,
        }))
        .sort((a, b) => b.relatedCount - a.relatedCount);
      
      const topCoreTableKeys = new Set(
        allCoreTables.slice(0, MAX_CORE_TABLES).map((t) => t.key)
      );
      
      // Reset isCore for tables not in final top 5
      state.tableData.forEach((table) => {
        if (table.isCore && !topCoreTableKeys.has(table.key)) {
          table.isCore = false;
          if (table.mapping > 0) {
            table.mapping--;
          }
        }
      });

    },
    addNewCoreTable: (
      state,
      action: PayloadAction<{ newRow: ITableData; projectId: number }>
    ) => {
      const newRow = {
        ...action.payload.newRow,
        isCore: true,
        isSelected: true,
      };
      const existingTable = state.tableData.find(
        (table) => table.key === newRow.key
      );
      if (!existingTable) {
        newRow.mapping = 1;
        state.tableData = [newRow, ...state.tableData];
      } else if (existingTable) {
        existingTable.isCore = true;
        existingTable.isSelected = true;
        existingTable.mapping = newRow.mapping + 1;
        state.tableData = [
          existingTable,
          ...state.tableData.filter((table) => table.key !== newRow.key),
        ];
      }
      newRow.relatedTables.forEach((relatedTable) => {
        const existingRelatedTable = state.tableData.find(
          (table) => table.key === relatedTable.key
        );
        if (existingRelatedTable) {
          existingRelatedTable.isSelected = true;
          existingRelatedTable.mapping++;
        } else {
          state.tableData.unshift({
            key: relatedTable.key,
            name: relatedTable.name,
            mapping: 1,
            isCore: false,
            isSelected: true,
            tableFoundInDB: false,
            relatedTables: [],
          });
        }
      });
    },
    removeNewCoreTables: (
      state,
      action: PayloadAction<{ tableKey: string; projectId: number }>
    ) => {
      const tableIndex = state.tableData.findIndex(
        (table) => table.key === action.payload.tableKey
      );

      if (tableIndex !== -1) {
        const table = state.tableData[tableIndex];

        if (table.tableFoundInDB) {
          table.isCore = false;
          if (table.mapping <= 1) {
            table.mapping = 0;
            table.isSelected = false;
          } else {
            table.mapping--;
          }
        } else {
          // Reduce the mapping by 1
          if (table.mapping > 1) {
            table.mapping--;
            table.isCore = false;
          } else {
            // Remove the table if mapping becomes 0
            state.tableData.splice(tableIndex, 1);
          }
        }

        // Find the related tables
        const relatedTables = table.relatedTables;
        relatedTables.forEach((relatedTable) => {
          const relatedTableIndex = state.tableData.findIndex(
            (table) => table.key === relatedTable.key
          );

          if (relatedTableIndex !== -1) {
            const relatedTable = state.tableData[relatedTableIndex];

            if (relatedTable.tableFoundInDB) {
              if (relatedTable.mapping <= 1) {
                relatedTable.mapping = 0;
                relatedTable.isSelected = false;
              } else {
                relatedTable.mapping--;
              }
            } else {
              // Reduce the mapping by 1
              if (relatedTable.mapping > 1) {
                relatedTable.mapping--;
              } else {
                // Remove the related table if mapping becomes 0
                state.tableData.splice(relatedTableIndex, 1);
              }
            }
          }
        });
      }
    },
    addNewRelatedTable: (
      state,
      action: PayloadAction<{
        newTable: IRelatedTable;
        coreTableKey: string;
        projectId: number;
      }>
    ) => {
      const { newTable, coreTableKey } = action.payload;

      // Find the coreTable in tableData
      const coreTableData = state.tableData.find(
        (table) => table.key === coreTableKey
      );

      if (coreTableData) {
        // Add the newTable to the relatedTables of coreTableData
        coreTableData.relatedTables.unshift(newTable);

        // Check if the newTable is already present in tableData
        const existingTable = state.tableData.find(
          (table) => table.key === newTable.key
        );

        if (existingTable) {
          if (existingTable.mapping === 0) {
            existingTable.mapping++;
            existingTable.isSelected = true;
          } else {
            existingTable.mapping++;
          }
        } else {
          // Add the newTable to tableData
          state.tableData.push({
            key: newTable.key,
            name: newTable.name,
            mapping: 1,
            isCore: false,
            isSelected: true,
            tableFoundInDB: false,
            relatedTables: [],
          });
        }
      }
    },
    removeNewRelatedTable: (
      state,
      action: PayloadAction<{
        coreTableKey: string;
        relatedTableKey: string;
        projectId: number;
      }>
    ) => {
      const { coreTableKey, relatedTableKey } = action.payload;

      const coreTableData = state.tableData.find(
        (table) => table.key === coreTableKey
      );

      if (coreTableData) {
        const relatedTableIndex = coreTableData.relatedTables.findIndex(
          (table) => table.key === relatedTableKey
        );

        if (relatedTableIndex !== -1) {
          coreTableData.relatedTables.splice(relatedTableIndex, 1);

          const relatedTableData = state.tableData.find(
            (table) => table.key === relatedTableKey
          );

          if (relatedTableData) {
            if (relatedTableData.tableFoundInDB) {
              if (relatedTableData.mapping <= 1) {
                relatedTableData.mapping = 0;
                relatedTableData.isSelected = false;
              } else {
                relatedTableData.mapping--;
              }
            } else {
              if (relatedTableData.mapping <= 1) {
                state.tableData = state.tableData.filter(
                  (table) => table.key !== relatedTableKey
                );
              } else {
                relatedTableData.mapping--;
              }
            }
          }
        }
      }
    },
  },
});

function processQueryLogData(queryLogData: IQueryLogResponse[]) {
  const allCoreTables = new Set<string>();
  const relationshipsMap = new Map<string, Set<string>>();

  queryLogData.forEach((queryLogResult) => {
    if (queryLogResult.core_tables && Array.isArray(queryLogResult.core_tables)) {
      queryLogResult.core_tables.forEach((tableName) => {
        allCoreTables.add(tableName);
      });
    }

    if (queryLogResult.relationships && Array.isArray(queryLogResult.relationships)) {
      queryLogResult.relationships.forEach((rel) => {
        const parentTable = rel?.parent_table;
        const childTable = rel?.child_table;

        if (!parentTable || !childTable) return;

        if (!relationshipsMap.has(parentTable)) {
          relationshipsMap.set(parentTable, new Set());
        }
        relationshipsMap.get(parentTable)!.add(childTable);
      });
    }
  });

  return { allCoreTables, relationshipsMap };
}

export function fetchBothTableAndQueryLogAction(
  projectId: number,
  runId: number | null
) {
  return async () => {
    dispatch(setIsTableDataLoading(true));

    const base = projectRunBasePath(projectId, runId);
    if (!base) {
      dispatch(setErrorMessage('No performance run selected.'));
      dispatch(setIsTableDataLoading(false));
      return;
    }

    try {
      const [tableInfoResponse, queryLogResponse] = await Promise.all([
        api().get(`${base}/db-tables`),
        api().get<IQueryLogResponse[]>(`${base}/query-log-tables`),
      ]);

      dispatch(setTableData(tableInfoResponse.data));
      if (tableInfoResponse.data.length === 0) {
        dispatch(setIsDataAvailable(false));
      } else {
        dispatch(setIsDataAvailable(true));
      }
      dispatch(setIsTableDataLoading(false));

      if (!queryLogResponse.data || queryLogResponse.data.length === 0) {
        dispatch(setIsQueryLogDataAvailable(false));
        return;
      }

      // Now process query-log analysis results 
      const { allCoreTables, relationshipsMap } = processQueryLogData(queryLogResponse.data);

      if (allCoreTables.size === 0) {
        dispatch(setIsQueryLogDataAvailable(false));
        return;
      }

      // Merge query log data after table data has been set
      dispatch(mergeQueryLogData({
        coreTables: allCoreTables,
        relationshipsMap: relationshipsMap,
      }));
      dispatch(setIsQueryLogDataAvailable(true));
    } catch (error: any) {
      dispatch(setErrorMessage(error.message));
      dispatch(setIsTableDataLoading(false));
    }
  };
}

export function updateTableInfoAction(
  updatedTableData: IRequestBody | undefined,
  projectId: number,
  runId: number | null
) {
  return async () => {
    const base = projectRunBasePath(projectId, runId);
    if (!base) return;
    try {
      await api().post(`${base}/select-table`, updatedTableData);
    } catch (error: any) {
      dispatch(setErrorMessage(error.displayMessage));
    } finally {
      dispatch(setIsTableDataLoading(false));
    }
  };
}
export const {
  resetCoreAndRelatedToInitial,
  setIsTableDataLoading,
  setErrorMessage,
  setTableData,
  addNewCoreTable,
  removeNewCoreTables,
  addNewRelatedTable,
  removeNewRelatedTable,
  setIsDataAvailable,
  setIsQueryLogDataAvailable,
  mergeQueryLogData,
} = coreAndRelatedTableSlice.actions;

export default coreAndRelatedTableSlice.reducer;
